import { useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api, sesion, type Resumen as TResumen, type Yo } from './api';
import { Riel } from './Riel';
import { Resumen } from './Resumen';
import { Verificaciones } from './Verificaciones';
import { FaceId } from './FaceId';
import { Admin } from './Admin';
import { Entrar } from './Entrar';
import { Vacio, Tarjeta } from './ui';

/** El tema del área de trabajo. El riel de tinta NO cambia: es la marca, no el tema. */
function useTema() {
  const [tema, setTema] = useState<'light' | 'dark'>(
    () => (localStorage.getItem('validate.tema') as 'light' | 'dark' | null) ?? 'light',
  );

  useEffect(() => {
    document.documentElement.dataset['theme'] = tema;
    localStorage.setItem('validate.tema', tema);
  }, [tema]);

  return { tema, alternar: () => setTema((t) => (t === 'light' ? 'dark' : 'light')) };
}

function Protegido({ children }: { children: ReactNode }) {
  const sitio = useLocation();
  if (!sesion.get()) return <Navigate to="/entrar" state={{ desde: sitio }} replace />;
  return <>{children}</>;
}

/**
 * El nombre corto de una persona, a partir de su correo.
 *
 * Función y no una expresión suelta, por lo que costó: antes decía
 * `yo?.email.split('@')[0]`, y ese `?.` protege `yo` pero NO `yo.email`. Cuando el servidor
 * devolvió una forma distinta a la que yo había supuesto, `email` llegó `undefined`, el
 * `.split` reventó y el panel entero salió en blanco — sin un solo error en el typecheck,
 * porque el tipo era una afirmación mía y era falsa.
 */
function nombreCorto(correo: string | undefined): string {
  return correo?.split('@')[0] ?? '—';
}

function Shell() {
  const [resumen, setResumen] = useState<TResumen | null>(null);
  const [yo, setYo] = useState<Yo | null>(null);
  const { tema, alternar } = useTema();

  useEffect(() => {
    api.resumen().then(setResumen).catch(() => undefined);
    api.yo().then(setYo).catch(() => undefined);
  }, []);

  const esAdmin = yo?.user.role === 'ADMIN';

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--bg)' }}>
      <Riel
        // El nombre real de la organización, no un texto fijo: el servidor ya lo manda.
        organizacion={yo?.organization.name ?? '—'}
        plan={resumen ? `Saldo ${resumen.moneda} ${resumen.saldo}` : '—'}
        usuario={yo?.user.name ?? nombreCorto(yo?.user.email)}
        rol={esAdmin ? 'Administrador' : 'Operador'}
        pendientes={resumen?.enRevision ?? 0}
        esAdmin={esAdmin}
      >
        <div className="px-3.5 pb-3">
          <button
            onClick={alternar}
            className="w-full rounded-lg px-3 py-2 text-left text-[12.5px]"
            style={{ color: 'var(--ink-text-weak)' }}
          >
            {tema === 'light' ? 'Modo oscuro' : 'Modo claro'}
          </button>
        </div>
      </Riel>

      <div className="flex flex-1 flex-col overflow-hidden">
        <Routes>
          <Route
            path="/"
            element={
              <Resumen resumen={resumen} nombre={yo?.user.name ?? nombreCorto(yo?.user.email)} />
            }
          />
          <Route path="/verificaciones" element={<Verificaciones resumen={resumen} />} />
          <Route path="/face-id" element={<FaceId />} />
          <Route path="/admin" element={<Admin />} />
          <Route
            path="*"
            element={
              <main className="flex-1 p-8">
                <Tarjeta>
                  <Vacio
                    titulo="Todavía no construimos esta pantalla"
                    texto="El primer producto es la verificación de identidad. Recetas, reglas, integración y saldo vienen después."
                  />
                </Tarjeta>
              </main>
            }
          />
        </Routes>
      </div>
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/entrar" element={<Entrar />} />
        <Route
          path="*"
          element={
            <Protegido>
              <Shell />
            </Protegido>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
